import { useState } from 'react';
import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { Text } from '@kula-ai/components';
import { PdfViewerWrapper } from '@/components/file-viewers';
import type { ResumeBlockProps } from './ResumeBlock.types';

// Inline PDF preview using core-web's existing PdfViewerWrapper so the
// rendering matches the candidate-job slider exactly. parsed_content from
// the kula_contact_resumes tool is shown as a structured digest above the
// preview — skill chips, current role, total experience — so the agent's
// prose answer + this card together cover the candidate's profile without
// the user opening the PDF unless they want to.

const formatRange = (from?: string, to?: string | null): string => {
  if (!from && !to) return '';
  return `${from ?? ''} — ${to ?? 'present'}`;
};

const hostnameOf = (raw: string): string => {
  try {
    return new URL(raw).hostname.replace(/^www\./, '');
  } catch {
    return raw;
  }
};

// Gmail-style attachment tile. Compact pill with a PDF icon, filename,
// content-type sub-label, "Preview" CTA, and a separate download link.
// Click body to expand into the full viewer.
const AttachmentChip = ({
  fileName,
  contentType,
  onClick,
  downloadHref,
}: {
  fileName: string;
  contentType: string;
  onClick: () => void;
  downloadHref: string;
}) => {
  const ext =
    contentType === 'application/pdf'
      ? 'PDF'
      : (contentType.split('/').pop() ?? 'FILE').toUpperCase();
  return (
    <Flex
      as="button"
      type="button"
      onClick={onClick}
      align="center"
      gap="10px"
      px="10px"
      py="8px"
      borderRadius="8px"
      border="1px solid"
      borderColor="secondary-200"
      bg="base-white"
      _hover={{ borderColor: 'primary-300', bg: 'primary-25' }}
      maxW="360px"
      textAlign="left"
      cursor="pointer"
    >
      <Flex
        align="center"
        justify="center"
        w="32px"
        h="40px"
        flexShrink={0}
        borderRadius="4px"
        bg="error-50"
        color="error-700"
        fontSize="10px"
        fontWeight="700"
      >
        {ext}
      </Flex>
      <Flex direction="column" flex="1" minW="0">
        <Text
          variant="span"
          className="text-secondary-900 text-xs font-medium truncate"
        >
          {fileName}
        </Text>
        <Flex gap="8px" align="center">
          <Text
            variant="span"
            className="text-primary-700 text-[11px] font-medium"
          >
            Click to preview
          </Text>
          <Text variant="span" className="text-secondary-300">
            ·
          </Text>
          <Box
            as="a"
            href={downloadHref}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            fontSize="11px"
            color="secondary-600"
            fontWeight="500"
            _hover={{ textDecoration: 'underline' }}
          >
            Download
          </Box>
        </Flex>
      </Flex>
    </Flex>
  );
};

const ContactChip = ({
  href,
  label,
  icon,
}: {
  href: string;
  label: string;
  icon?: string;
}) => (
  <Box
    as="a"
    href={href}
    target={href.startsWith('http') ? '_blank' : undefined}
    rel={href.startsWith('http') ? 'noreferrer' : undefined}
    px="8px"
    py="3px"
    borderRadius="full"
    border="1px solid"
    borderColor="secondary-200"
    bg="base-white"
    color="secondary-800"
    fontSize="11px"
    fontWeight="500"
    _hover={{
      bg: 'primary-25',
      borderColor: 'primary-300',
      color: 'primary-700',
    }}
  >
    {icon && (
      <Box as="span" mr="4px" color="secondary-500">
        {icon}
      </Box>
    )}
    {label}
  </Box>
);

export const ResumeBlock = ({ data }: ResumeBlockProps) => {
  const [activeIdx, setActiveIdx] = useState(0);
  // Gmail-style: attachment chip in the bubble, click opens a modal with the
  // full react-pdf viewer. `previewId` is the resume currently open in the
  // modal (null = closed).
  const [previewId, setPreviewId] = useState<number | null>(null);
  const resumes = data.resumes ?? [];
  if (resumes.length === 0) return null;
  const active = resumes[Math.min(activeIdx, resumes.length - 1)];
  if (!active) return null;
  const parsed = active.parsed ?? {};
  const skills = parsed.skills ?? [];
  const experience = parsed.experience ?? [];
  const education = parsed.education ?? [];

  return (
    <Box
      data-test-id="agentforge-resume-block"
      bg="base-white"
      border="1px solid"
      borderColor="secondary-100"
      borderRadius="10px"
      overflow="hidden"
    >
      {/* Header: name, current role, file selector (multi-resume case) */}
      <Flex
        align="center"
        justify="space-between"
        px="14px"
        py="10px"
        borderBottom="1px solid"
        borderColor="secondary-100"
        gap="10px"
      >
        <Flex direction="column" minW="0">
          <Text
            variant="span"
            className="text-secondary-900 font-semibold truncate"
          >
            {parsed.name ?? active.fileName}
          </Text>
          {parsed.currentJobTitle && (
            <Text
              variant="span"
              className="text-secondary-600 text-xs truncate"
            >
              {parsed.currentJobTitle}
              {typeof parsed.totalExperienceYears === 'number'
                ? ` · ${parsed.totalExperienceYears} yrs`
                : ''}
            </Text>
          )}
        </Flex>
        {resumes.length > 1 && (
          <Flex gap="6px" wrap="wrap" justify="flex-end">
            {resumes.map((r, i) => (
              <Box
                key={r.id}
                as="button"
                type="button"
                onClick={() => setActiveIdx(i)}
                px="8px"
                py="3px"
                borderRadius="full"
                border="1px solid"
                borderColor={i === activeIdx ? 'primary-300' : 'secondary-200'}
                bg={i === activeIdx ? 'primary-50' : 'base-white'}
                color={i === activeIdx ? 'primary-700' : 'secondary-700'}
                fontSize="11px"
                fontWeight="500"
              >
                {r.fileName}
              </Box>
            ))}
          </Flex>
        )}
      </Flex>

      {/* Contact chips — clickable mailto / tel / external links */}
      {(parsed.emails?.length ||
        parsed.mobileNumber ||
        parsed.linkedInUrl ||
        parsed.githubUrls?.length ||
        parsed.otherUrls?.length ||
        parsed.location ||
        parsed.country) && (
        <Flex
          wrap="wrap"
          gap="6px"
          px="14px"
          py="8px"
          borderBottom="1px solid"
          borderColor="secondary-50"
        >
          {(parsed.emails ?? []).slice(0, 3).map((e) => (
            <ContactChip
              key={`email-${e}`}
              href={`mailto:${e}`}
              label={e}
              icon="✉"
            />
          ))}
          {parsed.mobileNumber && (
            <ContactChip
              href={`tel:${parsed.mobileNumber.replace(/\s+/g, '')}`}
              label={parsed.mobileNumber}
              icon="📞"
            />
          )}
          {parsed.linkedInUrl && (
            <ContactChip href={parsed.linkedInUrl} label="LinkedIn" icon="in" />
          )}
          {(parsed.githubUrls ?? []).slice(0, 2).map((g) => (
            <ContactChip
              key={`gh-${g}`}
              href={g}
              label={
                g.replace(/^https?:\/\/(www\.)?github\.com\//, '') || 'GitHub'
              }
              icon="GH"
            />
          ))}
          {(parsed.otherUrls ?? []).slice(0, 3).map((u) => (
            <ContactChip
              key={`u-${u}`}
              href={u}
              label={hostnameOf(u)}
              icon="↗"
            />
          ))}
          {(parsed.location || parsed.country) && (
            <Box
              px="8px"
              py="3px"
              borderRadius="full"
              bg="secondary-50"
              color="secondary-700"
              fontSize="11px"
            >
              📍 {parsed.location ?? parsed.country}
            </Box>
          )}
        </Flex>
      )}

      {/* Skill chips */}
      {skills.length > 0 && (
        <Box
          px="14px"
          py="10px"
          borderBottom="1px solid"
          borderColor="secondary-50"
        >
          <Text
            variant="span"
            className="text-secondary-600 text-xs font-medium block mb-1"
          >
            Skills
          </Text>
          <Flex wrap="wrap" gap="4px">
            {skills.slice(0, 20).map((s) => (
              <Box
                key={s}
                px="8px"
                py="2px"
                borderRadius="full"
                bg="primary-25"
                color="primary-800"
                fontSize="11px"
                fontWeight="500"
              >
                {s}
              </Box>
            ))}
          </Flex>
        </Box>
      )}

      {/* Experience timeline */}
      {experience.length > 0 && (
        <Box
          px="14px"
          py="10px"
          borderBottom="1px solid"
          borderColor="secondary-50"
        >
          <Text
            variant="span"
            className="text-secondary-600 text-xs font-medium block mb-1"
          >
            Experience
          </Text>
          {experience.slice(0, 5).map((e, i) => (
            <Box key={i} mb="6px">
              <Text
                variant="span"
                className="text-secondary-900 text-xs font-medium"
              >
                {e.jobTitle ?? ''}
                {e.company ? ` · ${e.company}` : ''}
              </Text>
              <Text variant="span" className="text-secondary-500 text-xs block">
                {formatRange(e.from, e.to)}
                {e.isCurrent ? ' · current' : ''}
              </Text>
            </Box>
          ))}
        </Box>
      )}

      {/* Education */}
      {education.length > 0 && (
        <Box
          px="14px"
          py="10px"
          borderBottom="1px solid"
          borderColor="secondary-50"
        >
          <Text
            variant="span"
            className="text-secondary-600 text-xs font-medium block mb-1"
          >
            Education
          </Text>
          {education.slice(0, 3).map((ed, i) => (
            <Text
              key={i}
              variant="span"
              className="text-secondary-700 text-xs block"
            >
              {ed.qualification ?? ''}{' '}
              {ed.discipline ? `· ${ed.discipline}` : ''}
              {ed.school ? ` · ${ed.school}` : ''}
              {ed.from || ed.to ? ` · ${formatRange(ed.from, ed.to)}` : ''}
            </Text>
          ))}
        </Box>
      )}

      {/* Certifications */}
      {parsed.certifications && parsed.certifications.length > 0 && (
        <Box
          px="14px"
          py="8px"
          borderBottom="1px solid"
          borderColor="secondary-50"
        >
          <Text
            variant="span"
            className="text-secondary-600 text-xs font-medium block mb-1"
          >
            Certifications
          </Text>
          <Flex wrap="wrap" gap="4px">
            {parsed.certifications.map((c) => (
              <Box
                key={c}
                px="8px"
                py="2px"
                borderRadius="full"
                bg="secondary-50"
                color="secondary-700"
                fontSize="11px"
              >
                {c}
              </Box>
            ))}
          </Flex>
        </Box>
      )}

      {/* Applications — jobs this resume is attached to */}
      {active.attachedJobs && active.attachedJobs.length > 0 && (
        <Box
          px="14px"
          py="10px"
          borderBottom="1px solid"
          borderColor="secondary-50"
        >
          <Text
            variant="span"
            className="text-secondary-600 text-xs font-medium block mb-1"
          >
            Applications
          </Text>
          <Flex direction="column" gap="4px">
            {active.attachedJobs.map((j) => (
              <Box
                key={j.id}
                as="a"
                // Deep-link into the application's pipeline-stage view
                // when we have the appId; fall back to the bare job page
                // otherwise. Same-origin relative URL — the FE runs on
                // the kula ATS host so no env-var lookup needed.
                href={
                  j.appId
                    ? `/jobs/${j.id}?appId=${j.appId}&currentView=stage&tab=candidates`
                    : `/jobs/${j.id}`
                }
                target="_blank"
                rel="noopener noreferrer"
                px="8px"
                py="6px"
                borderRadius="6px"
                border="1px solid"
                borderColor="secondary-100"
                bg="secondary-25"
                _hover={{ bg: 'primary-25', borderColor: 'primary-200' }}
                display="block"
              >
                <Text
                  variant="span"
                  className="text-secondary-900 text-xs font-medium block"
                >
                  {j.name}
                </Text>
                <Text variant="span" className="text-secondary-500 text-[11px]">
                  Job ID {j.id}
                </Text>
              </Box>
            ))}
          </Flex>
        </Box>
      )}

      {/* PDF — Gmail-style attachment chip. Click opens a full-screen
          modal with the react-pdf viewer. */}
      <Box px="14px" py="10px">
        <AttachmentChip
          fileName={active.fileName}
          contentType={active.contentType}
          onClick={() => setPreviewId(active.id)}
          downloadHref={active.url}
        />
      </Box>

      {/* Full-screen preview modal */}
      {previewId !== null &&
        (() => {
          const target = resumes.find((r) => r.id === previewId);
          if (!target) return null;
          return (
            <Modal
              isOpen
              onClose={() => setPreviewId(null)}
              size="full"
              scrollBehavior="inside"
            >
              <ModalOverlay />
              <ModalContent m={0} h="100vh" borderRadius={0}>
                <ModalHeader
                  py="10px"
                  px="16px"
                  borderBottom="1px solid"
                  borderColor="secondary-100"
                >
                  <Flex align="center" justify="space-between" pr="40px">
                    <Text
                      variant="span"
                      className="text-secondary-900 font-semibold"
                    >
                      {target.fileName}
                    </Text>
                    <Box
                      as="a"
                      href={target.url}
                      target="_blank"
                      rel="noreferrer"
                      fontSize="12px"
                      color="primary-700"
                      fontWeight="500"
                      _hover={{ textDecoration: 'underline' }}
                    >
                      Open in new tab
                    </Box>
                  </Flex>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody p={0} bg="secondary-25">
                  <Box w="100%" h="100%">
                    <PdfViewerWrapper
                      url={target.url}
                      filename={target.fileName}
                      content_type={target.contentType}
                      showControls
                      isFullscreen
                      containerStyles={{ height: '100%' }}
                    />
                  </Box>
                </ModalBody>
              </ModalContent>
            </Modal>
          );
        })()}
    </Box>
  );
};
